/**
 * Squares hero header: NBA jersey/badge PNGs from the asset pipeline use more of the
 * canvas than NCAAMB college jerseys. Same outer box makes NBA look oversized; NBA-only
 * inner padding normalizes perceived size to match NCAA without changing NCAAMB.
 *
 * Typical source dimensions (jersey service):
 * - NBA: ~538×768 (portrait) — in a tall box, object-contain scales by height → looks huge vs NCAA.
 * - NCAAMB: ~1024×1024 (square) — in the same box, scales by width → inscribed square.
 *
 * CFB helmets and NFL team logos have the same problem in reverse: the Play header
 * box is a tall jersey frame (`124/138`). Those assets fill that width edge-to-edge
 * (they look fine in Pick'em's square box). Side-weighted inset only when the URL
 * is actually a helmet or an NFL `teams/` logo — jerseys stay full-bleed.
 */
export function isNbaSquaresSport(sport: string | undefined | null): boolean {
  return (sport ?? '').trim().toUpperCase() === 'NBA';
}

/** NCAA Football helmet artwork under picks-core/ncaafb/helmet/ (and the -low variant). */
export function isNcaafbHelmetAsset(url: string | undefined | null): boolean {
  return typeof url === 'string' && /\/ncaafb\/helmet(?:-low)?\//i.test(url);
}

/** NFL licensed team logos under picks-core/nfl/teams/ (and the -low variant). */
export function isNflTeamLogoAsset(url: string | undefined | null): boolean {
  return typeof url === 'string' && /\/nfl\/teams(?:-low)?\//i.test(url);
}

/** Padding inside the jersey frame (top, right, bottom, left) — pulls full-bleed NBA art in */
export const NBA_JERSEY_INNER_PADDING = '18% 16% 10% 16%' as const;

/** Lighter inset for NBA jerseys in results-modal period thumbnails (small square — full header padding would dominate). */
export const NBA_JERSEY_INNER_PADDING_RESULTS_THUMB = '12% 10% 8% 10%' as const;

/** Side-weighted inset so CFB helmets and NFL logos don't flush the header box / screen edges. */
export const HELMET_INNER_PADDING = '8% 14% 6% 14%' as const;

/** Lighter helmet inset for results-modal period thumbnails. */
export const HELMET_INNER_PADDING_RESULTS_THUMB = '6% 10% 4% 10%' as const;

/**
 * Inner padding for a Squares team asset, or `undefined` when the artwork should
 * fill the frame (NCAAMB jerseys, NFL jerseys, …).
 */
export function squaresTeamAssetPadding(
  sport: string | undefined | null,
  assetUrl: string | undefined | null,
  variant: 'header' | 'thumb' = 'header'
): string | undefined {
  if (isNbaSquaresSport(sport)) {
    return variant === 'thumb' ? NBA_JERSEY_INNER_PADDING_RESULTS_THUMB : NBA_JERSEY_INNER_PADDING;
  }
  if (isNcaafbHelmetAsset(assetUrl) || isNflTeamLogoAsset(assetUrl)) {
    return variant === 'thumb' ? HELMET_INNER_PADDING_RESULTS_THUMB : HELMET_INNER_PADDING;
  }
  return undefined;
}

/**
 * Results modal: NBA and NCAA use the same outer column + square fit; NBA images also use
 * NBA_JERSEY_INNER_PADDING + object-bottom (same idea as GameHeader). Thumbs use the same
 * square pixel box for every sport inside the w-8 h-10 row.
 */
export const RESULTS_MODAL_HERO_OUTER_W_PX = 132;
export const RESULTS_MODAL_HERO_OUTER_H_PX = 162;
export const RESULTS_MODAL_HERO_SQUARE_FIT_PX = 132;
export const RESULTS_MODAL_THUMB_SQUARE_FIT_PX = 28;
